import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, FilePlus2, UserPlus, ClipboardCheck, CheckCircle2, XCircle,
  ShieldAlert, ArrowRight, Inbox, Link2, Trash2,
} from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Input, Select, Label, Badge,
  EmptyState, Modal, Textarea, KPI, Alert, StatusDropdown,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import {
  listIntakeForms, createIntakeForm, updateIntakeForm,
  listIntakeSubmissions, updateIntakeSubmission,
  createContact, createMatter, runConflictCheck,
} from '../lib/db';
import { LEAD_STATUS, PRACTICE_AREAS } from '../lib/constants';
import { fullName, formatDate, formatRelative, classNames } from '../lib/formatters';
import { PlanGate } from '../contexts/PlanContext';
import toast from '../lib/toast';

const PIPELINE_STAGES = LEAD_STATUS.filter((s) => !['archived'].includes(s.value));

const DEFAULT_FIELDS = [
  { key: 'first_name', label: 'First name', type: 'text', required: true },
  { key: 'last_name',  label: 'Last name',  type: 'text', required: true },
  { key: 'email',      label: 'Email',      type: 'email' },
  { key: 'phone',      label: 'Phone',      type: 'tel' },
  { key: 'matter_description', label: 'Tell us about your matter', type: 'textarea' },
  { key: 'preferred_contact',  label: 'Preferred contact method', type: 'select', options: ['Email', 'Phone', 'Text'] },
];

function IntakePageInner() {
  const { firmId, refreshSignal } = useFirm();
  const [forms, setForms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pipeline'); // pipeline | forms | submissions
  const [showNewForm, setShowNewForm] = useState(false);
  const [showBuilder, setShowBuilder] = useState(null);
  const [showDetail, setShowDetail] = useState(null);

  const refresh = async () => {
    if (!firmId) { setLoading(false); return; }
    try {
      const [f, s] = await Promise.all([
        listIntakeForms(firmId),
        listIntakeSubmissions(firmId),
      ]);
      setForms(f.data || []);
      setSubmissions(s.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [firmId, refreshSignal]); // eslint-disable-line

  const kpis = useMemo(() => {
    const active = submissions.filter((s) => !['converted', 'rejected', 'archived'].includes(s.lead_status));
    const converted = submissions.filter((s) => s.lead_status === 'converted');
    const thisMonth = new Date(); thisMonth.setDate(1);
    const newThisMonth = submissions.filter((s) => new Date(s.submitted_at) >= thisMonth);
    const conversionRate = submissions.length ? Math.round((converted.length / submissions.length) * 100) : 0;
    return { active: active.length, converted: converted.length, newThisMonth: newThisMonth.length, conversionRate };
  }, [submissions]);

  const byStage = useMemo(() => {
    const groups = {};
    for (const s of PIPELINE_STAGES) groups[s.value] = [];
    for (const sub of submissions) {
      const stage = sub.lead_status || 'new';
      if (!groups[stage]) continue;
      groups[stage].push(sub);
    }
    return groups;
  }, [submissions]);

  const moveSubmission = async (id, lead_status) => {
    setSubmissions((prev) => prev.map((s) => s.id === id ? { ...s, lead_status } : s));
    await updateIntakeSubmission(id, { lead_status });
  };

  if (loading) return <div className="p-8 text-center text-sm text-slate-500">Loading intake…</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Inbox size={22} /> Client Intake
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Lead pipeline, intake forms, and conflict checks
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={FilePlus2} onClick={() => setShowNewForm(true)}>New intake form</Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Active leads"       value={kpis.active}         icon={Inbox}          tone="indigo" />
        <KPI label="New this month"     value={kpis.newThisMonth}   icon={UserPlus}       tone="green" />
        <KPI label="Converted"          value={kpis.converted}      icon={CheckCircle2}   tone="indigo" />
        <KPI label="Conversion rate"    value={`${kpis.conversionRate}%`} icon={ClipboardCheck} tone="amber" />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-800 -mb-px">
        <div className="flex gap-4">
          {[
            { key: 'pipeline',    label: 'Pipeline' },
            { key: 'submissions', label: 'All Submissions' },
            { key: 'forms',       label: `Forms (${forms.length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setView(t.key)}
              className={classNames(
                'px-1 py-3 text-sm border-b-2 transition',
                view === t.key
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-medium'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-200',
              )}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {view === 'pipeline' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {PIPELINE_STAGES.map((stage) => (
            <div key={stage.value} className="space-y-2">
              <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-2">
                  <Badge tone={stage.tone}>{stage.label}</Badge>
                  <span className="text-xs text-slate-500">{byStage[stage.value]?.length || 0}</span>
                </div>
              </div>
              <div className="space-y-2 min-h-[120px]">
                {(byStage[stage.value] || []).map((s) => {
                  const payload = s.data || {};
                  const name = payload.first_name || payload.last_name
                    ? `${payload.first_name || ''} ${payload.last_name || ''}`.trim()
                    : payload.name || 'Unnamed lead';
                  return (
                    <button
                      key={s.id}
                      onClick={() => setShowDetail(s)}
                      className="w-full text-left bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-3 hover:shadow-md transition"
                    >
                      <p className="font-medium text-sm">{name}</p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{payload.email || payload.phone || '—'}</p>
                      {payload.matter_description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{payload.matter_description}</p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-2">{formatRelative(s.submitted_at)}</p>
                    </button>
                  );
                })}
                {!byStage[stage.value]?.length && (
                  <div className="text-xs text-slate-400 text-center py-6 border border-dashed border-slate-200 dark:border-slate-800 rounded-lg">
                    No leads
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'submissions' && (
        <Card>
          <CardBody className="p-0">
            {!submissions.length ? (
              <EmptyState icon={Inbox} title="No submissions yet" description="Share an intake form link to start collecting leads." />
            ) : (
              <>
                {/* Mobile cards */}
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {submissions.map((s) => {
                    const p = s.data || {};
                    return (
                      <li key={s.id} className="px-4 py-3" onClick={() => setShowDetail(s)}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{p.first_name} {p.last_name}</p>
                            <p className="text-xs text-slate-500">{p.email || p.phone || '—'}</p>
                            <p className="text-xs text-slate-400">{s.form?.name || '—'} · {formatDate(s.submitted_at)}</p>
                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            <StatusDropdown value={s.lead_status} options={LEAD_STATUS} onChange={(ns) => moveSubmission(s.id, ns)} />
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                    <tr>
                      <th className="text-left px-6 py-2 font-medium">Submitted</th>
                      <th className="text-left px-6 py-2 font-medium">Lead</th>
                      <th className="text-left px-6 py-2 font-medium">Contact</th>
                      <th className="text-left px-6 py-2 font-medium">Form</th>
                      <th className="text-left px-6 py-2 font-medium">Status</th>
                      <th className="text-left px-6 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {submissions.map((s) => {
                      const p = s.data || {};
                      // eslint-disable-next-line no-unused-vars
                      const _status = LEAD_STATUS.find((l) => l.value === s.lead_status);
                      return (
                        <tr key={s.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => setShowDetail(s)}>
                          <td className="px-6 py-3 text-slate-500">{formatDate(s.submitted_at)}</td>
                          <td className="px-6 py-3 font-medium">{p.first_name} {p.last_name}</td>
                          <td className="px-6 py-3 text-slate-500">{p.email || p.phone || '—'}</td>
                          <td className="px-6 py-3 text-slate-500">{s.form?.name || '—'}</td>
                          <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                            <StatusDropdown
                              value={s.lead_status}
                              options={LEAD_STATUS}
                              onChange={(newStatus) => moveSubmission(s.id, newStatus)}
                            />
                          </td>
                          <td className="px-6 py-3"><Button size="sm" variant="ghost">Open <ArrowRight size={12} /></Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}

      {view === 'forms' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!forms.length ? (
            <Card className="md:col-span-2">
              <EmptyState
                icon={FilePlus2}
                title="No intake forms yet"
                description="Create an intake form to collect lead data. You'll get a public link to share."
                action={<Button icon={Plus} onClick={() => setShowNewForm(true)}>New intake form</Button>}
              />
            </Card>
          ) : forms.map((f) => (
            <Card key={f.id}>
              <CardBody>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-semibold">{f.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{(f.fields || []).length} fields · {f.practice_area || 'General'}</p>
                  </div>
                  <Badge tone={f.is_active ? 'green' : 'slate'}>{f.is_active ? 'Active' : 'Draft'}</Badge>
                </div>
                {f.description && <p className="text-sm text-slate-600 dark:text-slate-300 mt-3">{f.description}</p>}
                <div className="mt-4 flex items-center gap-2">
                  <Button size="sm" variant="secondary" icon={Link2} onClick={() => {
                    const url = `${window.location.origin}/#/intake/${f.id}`;
                    navigator.clipboard.writeText(url);
                    toast.info('Public intake link copied to clipboard');
                  }}>Copy public link</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowBuilder(f)}>Edit</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      {showNewForm && (
        <FormBuilderModal
          open={showNewForm}
          onClose={() => setShowNewForm(false)}
          firmId={firmId}
          onSaved={(f) => { setForms((prev) => [f, ...prev]); setShowNewForm(false); }}
        />
      )}
      {showBuilder && (
        <FormBuilderModal
          open={!!showBuilder}
          form={showBuilder}
          onClose={() => setShowBuilder(null)}
          firmId={firmId}
          onSaved={(f) => { setForms((prev) => prev.map((x) => x.id === f.id ? f : x)); setShowBuilder(null); }}
        />
      )}
      {showDetail && (
        <SubmissionDetailModal
          submission={showDetail}
          firmId={firmId}
          onClose={() => setShowDetail(null)}
          onMove={moveSubmission}
          onConverted={(sub) => {
            setSubmissions((prev) => prev.map((x) => x.id === sub.id ? sub : x));
            setShowDetail(null);
          }}
        />
      )}
    </div>
  );
}

// ================ Form Builder Modal ================
const FormBuilderModal = ({ open, onClose, firmId, form, onSaved }) => {
  const isEdit = !!form;
  const [data, setData] = useState(form || {
    name: '', description: '', practice_area: '',
    fields: DEFAULT_FIELDS, is_active: true,
  });
  const [saving, setSaving] = useState(false);

  const addField = () => setData({
    ...data,
    fields: [...(data.fields || []), { key: `field_${Date.now()}`, label: 'New field', type: 'text' }],
  });

  const updateField = (idx, patch) => {
    const next = [...(data.fields || [])];
    next[idx] = { ...next[idx], ...patch };
    setData({ ...data, fields: next });
  };

  const removeField = (idx) => {
    if (!window.confirm('Remove this field from the form? This cannot be undone.')) return;
    const next = [...(data.fields || [])];
    next.splice(idx, 1);
    setData({ ...data, fields: next });
  };

  const save = async () => {
    if (!data.name) { toast.warning('Form needs a name'); return; }
    setSaving(true);
    let res;
    if (isEdit) res = await updateIntakeForm(form.id, data);
    else res = await createIntakeForm(firmId, data);
    setSaving(false);
    if (res.error) { toast.error(res.error.message); return; }
    onSaved(res.data);
  };

  return (
    <Modal open={open} onClose={onClose} size="xl" title={isEdit ? 'Edit intake form' : 'New intake form'} footer={
      <>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} onClick={save}>{isEdit ? 'Save changes' : 'Create form'}</Button>
      </>
    }>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label required>Form name</Label>
          <Input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="New Client Intake — Family Law" />
        </div>
        <div>
          <Label>Practice area</Label>
          <Select placeholder="General" options={PRACTICE_AREAS} value={data.practice_area || ''}
            onChange={(e) => setData({ ...data, practice_area: e.target.value })} />
        </div>
        <div>
          <Label>Status</Label>
          <Select options={[
            { value: 'true', label: 'Active — accepting submissions' },
            { value: 'false', label: 'Draft — not public' },
          ]} value={String(data.is_active)} onChange={(e) => setData({ ...data, is_active: e.target.value === 'true' })} />
        </div>
        <div className="sm:col-span-2">
          <Label>Description</Label>
          <Textarea rows={2} value={data.description || ''} onChange={(e) => setData({ ...data, description: e.target.value })} />
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold">Fields ({(data.fields || []).length})</h4>
          <Button size="sm" variant="secondary" icon={Plus} onClick={addField}>Add field</Button>
        </div>
        <div className="space-y-2">
          {(data.fields || []).map((f, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2 p-2 border border-slate-200 dark:border-slate-800 rounded-lg">
              <Input className="flex-1 min-w-[150px]" value={f.label}
                onChange={(e) => updateField(idx, { label: e.target.value })} />
              <Select className="w-40" options={[
                'text', 'email', 'tel', 'textarea', 'date', 'number', 'select',
              ].map((v) => ({ value: v, label: v }))} value={f.type}
                onChange={(e) => updateField(idx, { type: e.target.value })} />
              <Input className="w-32" placeholder="field_key" value={f.key}
                onChange={(e) => updateField(idx, { key: e.target.value })} />
              <label className="text-xs flex items-center gap-1 text-slate-500">
                <input type="checkbox" checked={!!f.required}
                  onChange={(e) => updateField(idx, { required: e.target.checked })} /> required
              </label>
              <button onClick={() => removeField(idx)} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

// ================ Submission Detail Modal ================
const SubmissionDetailModal = ({ submission, firmId, onClose, onMove, onConverted }) => {
  const payload = submission.data || {};
  const [conflicts, setConflicts] = useState(null);
  const [checking, setChecking] = useState(false);
  const [converting, setConverting] = useState(false);

  const runCheck = async () => {
    setChecking(true);
    const { data } = await runConflictCheck(firmId, {
      name: `${payload.first_name || ''} ${payload.last_name || ''}`.trim(),
      email: payload.email, phone: payload.phone,
    });
    setConflicts(data || []);
    setChecking(false);
  };

  const convertToClient = async () => {
    setConverting(true);
    // 1. Create contact
    const { data: contact, error: cErr } = await createContact(firmId, {
      first_name: payload.first_name, last_name: payload.last_name,
      email: payload.email, phone: payload.phone,
      contact_type: 'client',
    });
    if (cErr) { toast.error(cErr.message); setConverting(false); return; }
    // 2. Create matter
    const year = new Date().getFullYear();
    const { data: matter, error: mErr } = await createMatter(firmId, {
      title: payload.matter_title || `Matter for ${payload.first_name} ${payload.last_name}`.trim(),
      description: payload.matter_description || '',
      client_id: contact.id,
      matter_number: `${year}-${String(Date.now()).slice(-4)}`,
      status: 'open',
      priority: 'medium',
      fee_arrangement: 'hourly',
    });
    if (mErr) { toast.error(mErr.message); setConverting(false); return; }
    // 3. Mark submission converted
    const { data: updated } = await updateIntakeSubmission(submission.id, {
      status: 'converted', converted_contact_id: contact.id, converted_matter_id: matter.id,
    });
    setConverting(false);
    onConverted(updated);
    toast.success(`Converted to client — matter ${matter.matter_number}`);
  };

  return (
    <Modal open={!!submission} onClose={onClose} size="lg" title="Lead detail" footer={
      <>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button variant="secondary" icon={XCircle} onClick={() => { onMove(submission.id, 'rejected'); onClose(); }}>
          Reject
        </Button>
        <Button loading={converting} icon={UserPlus} onClick={convertToClient} disabled={submission.lead_status === 'converted'}>
          {submission.lead_status === 'converted' ? 'Already converted' : 'Convert to client + matter'}
        </Button>
      </>
    }>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Label>Pipeline stage</Label>
          <Select value={submission.lead_status} options={LEAD_STATUS.map((s) => ({ value: s.value, label: s.label }))}
            onChange={(e) => onMove(submission.id, e.target.value)} />
          <span className="text-xs text-slate-500">Submitted {formatDate(submission.submitted_at)}</span>
        </div>

        <Card>
          <CardHeader title="Submitted information" />
          <CardBody>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {Object.entries(payload).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs uppercase tracking-wide text-slate-500">{k.replace(/_/g, ' ')}</dt>
                  <dd className="text-slate-900 dark:text-slate-100 mt-0.5 whitespace-pre-wrap">{String(v || '—')}</dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Conflict check"
            subtitle="Name · email · phone vs. existing contacts"
            action={<Button size="sm" variant="secondary" icon={ShieldAlert} loading={checking} onClick={runCheck}>
              Run check
            </Button>}
          />
          <CardBody>
            {conflicts == null ? (
              <p className="text-sm text-slate-500">Run a conflict check before converting this lead.</p>
            ) : conflicts.length === 0 ? (
              <Alert tone="success" title="No conflicts found">
                No existing contacts match by name, email, or phone. Safe to convert.
              </Alert>
            ) : (
              <div className="space-y-2">
                <Alert tone="warning" title={`${conflicts.length} potential conflict${conflicts.length === 1 ? '' : 's'}`}>
                  Review carefully before accepting this lead.
                </Alert>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {conflicts.map((h, idx) => (
                    <li key={idx} className="py-2 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{fullName(h.contact)}</p>
                        <p className="text-xs text-slate-500">{h.contact.email || '—'} · {h.contact.contact_type}</p>
                      </div>
                      <Badge tone="amber">{h.reason}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </Modal>
  );
};

export default function IntakePage() {
  return <PlanGate feature="intake"><IntakePageInner /></PlanGate>;
}
